import type { MessageKey } from "@/i18n/catalog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { getConfig } from "@/config";
import { getUser } from "@/auth/userManager";
import { ApiError, type ApiErrorBody } from "./client";

/** A saved per-bank column mapping (`model.md` §3.10). */
export interface ImportProfile {
  id: string;
  name: string;
  format: string;
  /** Source column → model field, the direction the model states. */
  field_mapping: Record<string, string>;
  date_format?: string;
  decimal_separator?: string;
  thousands_separator?: string;
  encoding?: string;
  delimiter?: string;
  header_row_count?: number;
  amount_mode?: string;
  default_account_id?: string;
  /** Counted on committed imports, never on previews. */
  times_used: number;
  last_used_at?: string;
}

/** One parsed line, as the mapping screen shows it. */
export interface PreviewRow {
  /** The line in the source file, which is what somebody looks up in their spreadsheet. */
  number: number;
  /** The source line verbatim, so the table can show what the file said. */
  raw: Record<string, string>;
  booked_date?: string;
  value_date?: string;
  amount_minor: number;
  currency_code?: string;
  payee?: string;
  reference?: string;
  note?: string;
  /** Empty when the row parsed. A row that failed is still a row. */
  error?: string;
  /**
   * This line looks like one already in the ledger.
   *
   * A flag, never a rejection (`model.md` §3.10): two €3 coffees at the same
   * bakery on the same day are a legitimate pair, and refusing the second loses
   * real money from the ledger.
   */
  duplicates?: DuplicateMatch[];
}

/** A booking a candidate line may repeat. */
export interface DuplicateMatch {
  transaction_id: string;
  booked_date: string;
  amount_minor: number;
  payee?: string;
  /**
   * `bank_reference` when the bank itself says these are the same payment, and
   * `content` when only the contents match — an assertion and a question.
   */
  strength: "bank_reference" | "content";
}

export interface Preview {
  columns: string[];
  delimiter: string;
  encoding: string;
  /** Proposed from the column names, offered beside the pickers and never written in. */
  suggested_mapping: Record<string, string>;
  suggested_amount_mode: string;
  mappable_fields: string[];
  /** What was used — for a file with a preamble, what the file's own shape suggested. */
  header_row_count: number;
  rows: PreviewRow[];
  rows_total: number;
  rows_failed: number;
  /** Flagged lines across the whole file, not only the ones returned. */
  rows_duplicate: number;
  rows_duplicate_hard: number;
  /** What duplicates were checked against; absent means the question could not be asked. */
  account_id?: string;
}

/** The settings a preview or a saved profile carries. */
export interface ProfileSettings {
  name?: string;
  format?: string;
  field_mapping?: Record<string, string>;
  date_format?: string;
  decimal_separator?: string;
  thousands_separator?: string;
  encoding?: string;
  delimiter?: string;
  header_row_count?: number;
  amount_mode?: string;
  default_account_id?: string;
}

interface List<T> {
  items: T[];
}

export const importProfilesQueryKey = (householdId: string) =>
  ["households", householdId, "import-profiles"] as const;

export function useImportProfiles(householdId: string | undefined) {
  return useQuery({
    queryKey: importProfilesQueryKey(householdId ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<List<ImportProfile>>(
        `/api/v1/households/${householdId}/import-profiles`,
        { signal },
      ),
    enabled: Boolean(householdId),
  });
}

export function useSaveImportProfile(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      settings,
    }: {
      id?: string;
      settings: ProfileSettings;
    }) =>
      apiFetch<ImportProfile>(
        `/api/v1/households/${householdId}/import-profiles${id ? `/${id}` : ""}`,
        { method: id ? "PUT" : "POST", body: settings },
      ),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: importProfilesQueryKey(householdId),
      }),
  });
}

export function useDeleteImportProfile(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) =>
      apiFetch<void>(
        `/api/v1/households/${householdId}/import-profiles/${profileId}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: importProfilesQueryKey(householdId),
      }),
  });
}

/**
 * Asks what a file would become, without importing it.
 *
 * Multipart rather than JSON: a statement is a file, and base64 in a JSON body
 * would cost a third of its size again for no benefit. `apiFetch` is not reused
 * because it sets a JSON content type — the browser has to set this one itself,
 * so that it can add the multipart boundary.
 */
export async function previewImport(
  householdId: string,
  file: File,
  options: {
    profileId?: string;
    accountId?: string;
    settings?: ProfileSettings;
  } = {},
): Promise<Preview> {
  const { apiBaseUrl } = getConfig();
  const user = await getUser();

  const body = new FormData();
  body.append("file", file);
  if (options.profileId) body.append("profile_id", options.profileId);
  if (options.accountId) body.append("account_id", options.accountId);
  if (options.settings)
    body.append("settings", JSON.stringify(options.settings));

  const headers: Record<string, string> = { Accept: "application/json" };
  if (user?.access_token) {
    headers["Authorization"] = `Bearer ${user.access_token}`;
  }

  const response = await fetch(
    `${apiBaseUrl}/api/v1/households/${householdId}/import-profiles/preview`,
    { method: "POST", headers, body },
  );

  if (!response.ok) {
    let errorBody: ApiErrorBody | undefined;
    try {
      errorBody = (await response.json()) as ApiErrorBody;
    } catch {
      // A non-JSON error body (a proxy 413, say) is not exceptional.
    }
    throw new ApiError(response.status, errorBody, response.statusText);
  }
  return (await response.json()) as Preview;
}

/** The fields a column can be mapped onto, in German, for the pickers. */
/**
 * What each mappable field is called, as a message key.
 *
 * The *field names* are the API's vocabulary and never change; what a German or
 * English reader calls them is the interface's (K-36). A statement's own column
 * headings are neither — they are whatever the bank wrote in the file, and are
 * shown verbatim.
 */
export const FIELD_LABELS: Record<string, MessageKey> = {
  booked_date: "field.booked_date",
  value_date: "field.value_date",
  amount: "field.amount",
  debit: "field.debit",
  credit: "field.credit",
  sign: "field.sign",
  currency: "field.currency",
  payee: "field.payee",
  reference: "field.reference",
  note: "field.note",
  bank_reference: "field.bank_reference",
};

/** One import, as a unit (`model.md` §3.10). */
export interface ImportBatch {
  id: string;
  import_profile_id?: string;
  account_id?: string;
  format: string;
  source_filename: string;
  status: string;
  rows_total: number;
  rows_imported: number;
  rows_skipped_duplicate: number;
  rows_skipped_by_user: number;
  rows_failed: number;
  committed_at?: string;
  /** K-32's. A screen listing imports has to say which were undone. */
  reverted_at?: string;
  created_at: string;
}

/** One source line and what it became. */
export interface ImportRowRecord {
  row_number: number;
  raw: Record<string, string>;
  outcome: "imported" | "skipped_duplicate" | "skipped_by_user" | "failed";
  transaction_id?: string;
  duplicate_of_transaction_id?: string;
  error_message?: string;
}

export interface ImportBatchDetail {
  batch: ImportBatch;
  rows: ImportRowRecord[];
}

export const importsQueryKey = (householdId: string) =>
  ["households", householdId, "imports"] as const;

export function useImports(householdId: string | undefined) {
  return useQuery({
    queryKey: importsQueryKey(householdId ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<List<ImportBatch>>(`/api/v1/households/${householdId}/imports`, {
        signal,
      }),
    enabled: Boolean(householdId),
  });
}

export interface CommitOptions {
  accountId: string;
  profileId?: string;
  settings?: ProfileSettings;
  /** Line numbers the household chose to leave out. */
  skipRows?: number[];
  /** Leave out every line already in the ledger. */
  skipDuplicates?: boolean;
}

/**
 * Imports the file for real.
 *
 * The file is sent again rather than referenced from the preview, and that is
 * deliberate: a preview holds nothing server-side, so there is no half-import to
 * leak or expire — and re-reading means the rows that land are the rows of *this*
 * file.
 */
export async function commitImport(
  householdId: string,
  file: File,
  options: CommitOptions,
): Promise<ImportBatchDetail> {
  const { apiBaseUrl } = getConfig();
  const user = await getUser();

  const body = new FormData();
  body.append("file", file);
  body.append("account_id", options.accountId);
  if (options.profileId) body.append("profile_id", options.profileId);
  if (options.settings)
    body.append("settings", JSON.stringify(options.settings));
  if (options.skipRows?.length)
    body.append("skip_rows", options.skipRows.join(","));
  if (options.skipDuplicates) body.append("skip_duplicates", "true");

  const headers: Record<string, string> = { Accept: "application/json" };
  if (user?.access_token) {
    headers["Authorization"] = `Bearer ${user.access_token}`;
  }

  const response = await fetch(
    `${apiBaseUrl}/api/v1/households/${householdId}/imports`,
    { method: "POST", headers, body },
  );

  if (!response.ok) {
    let errorBody: ApiErrorBody | undefined;
    try {
      errorBody = (await response.json()) as ApiErrorBody;
    } catch {
      // A non-JSON error body is not exceptional.
    }
    throw new ApiError(response.status, errorBody, response.statusText);
  }
  return (await response.json()) as ImportBatchDetail;
}

/** What each outcome is called on screen (`model.md` §3.10). */
export const OUTCOME_LABELS: Record<ImportRowRecord["outcome"], MessageKey> = {
  imported: "outcome.imported",
  skipped_duplicate: "outcome.skipped_duplicate",
  skipped_by_user: "outcome.skipped_by_user",
  failed: "outcome.failed",
};

/** What an undo did, with the batch's provenance unchanged beside it. */
export interface RevertResult {
  batch: ImportBatch;
  /** How many transactions were soft-deleted, or restored. */
  affected: number;
  rows: ImportRowRecord[];
}

/**
 * Undoes an import, or puts it back.
 *
 * `model.md` §5.5: soft-delete exactly the batch's transactions, set
 * `reverted_at`, touch nothing else — and the operation is itself reversible,
 * because the deletion is soft.
 */
export function useImportAction(householdId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      batchId,
      action,
    }: {
      batchId: string;
      action: "revert" | "restore";
    }) =>
      apiFetch<RevertResult>(
        `/api/v1/households/${householdId}/imports/${batchId}/${action}`,
        { method: "POST", body: {} },
      ),
    // An undo moves balances, the ledger, the dashboard and the budgets — every
    // one of them derives from transactions that just stopped being live.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["households", householdId] }),
  });
}

/**
 * A shipped starting point for one bank (K-34).
 *
 * It carries the four settings no column-name heuristic can infer: the
 * delimiter, the encoding, the header row count and the amount mode. Kontora can
 * tell that "Buchungstag" is a booking date because the word says so; it cannot
 * tell that the file is Latin-1, and getting that wrong produces a statement
 * full of mojibake that nothing downstream would object to.
 *
 * `note` says what to check, because a starting point that pretends to be
 * certain is worse than one that admits what it is.
 */
export interface StarterProfile {
  key: string;
  bank: string;
  note: string;
  settings: ProfileSettings;
}

export function useStarterProfiles(householdId: string | undefined) {
  return useQuery({
    queryKey: [...importProfilesQueryKey(householdId ?? ""), "starters"],
    queryFn: ({ signal }) =>
      apiFetch<List<StarterProfile>>(
        `/api/v1/households/${householdId}/import-profiles/starters`,
        { signal },
      ),
    enabled: Boolean(householdId),
    // Shipped with the product: it changes when the product does, not while
    // somebody is looking at it.
    staleTime: Infinity,
  });
}
